// js/components/ProductVideo.js
import Delegate from 'ftdomdelegate';
import { Responsive } from '../utils.js';

export class ProductVideo {
  constructor(element, enableVideoLooping) {
    this.element = element;
    this.delegateElement = new Delegate(this.element);
    this.enableVideoLooping = enableVideoLooping;
    this.player = null;
    this.shouldPlayWhenReady = false;
    this.muteCheckInterval = null;
    this.boundHandlePostMessage = null;
    
    // Listen for unmute events from iframe
    if (this.element.getAttribute("data-media-type") === "external_video") {
      this.boundHandlePostMessage = this._handlePostMessage.bind(this);
      window.addEventListener('message', this.boundHandlePostMessage);
    }
    
    switch (this.element.getAttribute("data-media-type")) {
      case "video":
        let stylesheet = document.createElement("link");
        stylesheet.rel = "stylesheet";
        stylesheet.href = "https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css";
        document.head.appendChild(stylesheet);
        window.Shopify.loadFeatures([{
          name: "video-ui",
          version: "1.0",
          onLoad: this._setupHtml5Video.bind(this)
        }]);
        break;
      case "external_video":
        this._setupExternalVideo();
        break;
    }
  }

  destroy() {
    if (this.player && typeof this.player.destroy === 'function') {
      this.player.destroy();
    }
    // Remove postMessage listener
    if (this.boundHandlePostMessage) {
      window.removeEventListener('message', this.boundHandlePostMessage);
      this.boundHandlePostMessage = null;
    }
    // Stop mute check interval
    if (this.muteCheckInterval) {
      clearTimeout(this.muteCheckInterval);
      this.muteCheckInterval = null;
    }
  }

  hasBeenSelected(isInitialLoading) {
    // For external_video, always play when slide changes
    if (this.element.getAttribute("data-media-type") === "external_video") {
      this.shouldPlayWhenReady = true;
      this.play();
    } else if (Responsive.matchesBreakpoint("supports-hover") && !isInitialLoading) {
      this.play();
    }
  }

  hasBeenDeselected() {
    this.shouldPlayWhenReady = false;
    this.pause();
  }

  play() {
    if (!this.player) return;
    
    switch (this.element.getAttribute("data-media-type")) {
      case "video":
        this.player.play();
        break;
      case "external_video":
        if (this.player && this.player.iframe) {
          const iframe = this.player.iframe;
          const src = iframe.src || iframe.getAttribute('src');
          
          if (this.player.host === "youtube") {
            // Check if user has previously unmuted
            const hasUnmuted = sessionStorage.getItem('video_unmuted') === 'true';
            
            // Add autoplay=1 to YouTube URL
            let newSrc = src;
            
            // Add or replace autoplay parameter
            if (src.includes('autoplay=')) {
              newSrc = newSrc.replace(/autoplay=\d+/, 'autoplay=1');
            } else {
              newSrc = newSrc.includes('?') ? newSrc + '&autoplay=1' : newSrc + '?autoplay=1';
            }
            
            // Add or replace mute parameter - only mute if user hasn't unmuted yet
            const muteValue = hasUnmuted ? '0' : '1';
            if (src.includes('mute=')) {
              newSrc = newSrc.replace(/mute=\d+/, `mute=${muteValue}`);
            } else {
              newSrc = newSrc.includes('?') ? newSrc + `&mute=${muteValue}` : newSrc + `?mute=${muteValue}`;
            }
            
            if (newSrc !== src) {
              iframe.src = newSrc;
            }
          } else if (this.player.host === "vimeo") {
            // Use postMessage API for Vimeo
            if (iframe.contentWindow && iframe.contentWindow.postMessage) {
              iframe.contentWindow.postMessage('{"method":"play"}', '*');
            }
          }
        }
        break;
    }
  }

  pause() {
    if (!this.player) return;
    
    switch (this.element.getAttribute("data-media-type")) {
      case "video":
        this.player.pause();
        break;
      case "external_video":
        if (this.player && this.player.iframe) {
          const iframe = this.player.iframe;
          const src = iframe.src || iframe.getAttribute('src');
          
          if (this.player.host === "youtube") {
            // Remove autoplay=1 and mute=1 from YouTube URL
            let newSrc = src.replace(/autoplay=1/, 'autoplay=0');
            newSrc = newSrc.replace(/mute=1/, 'mute=0');
            if (newSrc !== src) {
              iframe.src = newSrc;
            }
          } else if (this.player.host === "vimeo") {
            // Use postMessage API for Vimeo
            if (iframe.contentWindow && iframe.contentWindow.postMessage) {
              iframe.contentWindow.postMessage('{"method":"pause"}', '*');
            }
          }
        }
        break;
    }
  }

  _setupHtml5Video() {
    this.player = new Shopify.Plyr(this.element.querySelector("video"), {
      controls: [
        "play",
        "progress",
        "mute",
        "volume",
        "play-large",
        "fullscreen"
      ],
      loop: { active: this.enableVideoLooping },
      hideControlsOnPause: true,
      clickToPlay: true,
      iconUrl: "//cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.svg",
      tooltips: {
        controls: false,
        seek: true
      }
    });
    this.player.on("play", () => {
      this.element.dispatchEvent(new CustomEvent("video:played", { bubbles: true }));
    });
    this.player.on("pause", () => {
      this.element.dispatchEvent(new CustomEvent("video:paused", { bubbles: true }));
    });
  }

  _setupExternalVideo() {
    // For external_video, we don't need YT.Player wrapper - just control iframe directly
    const iframe = this.element.querySelector("iframe");
    if (iframe) {
      this.player = {
        iframe: iframe,
        host: this.element.getAttribute("data-media-host"),
        videoId: this.element.getAttribute("data-video-id")
      };
      
      // Listen for clicks on the iframe to detect unmute
      if (this.player.host === "youtube") {
        // Check URL periodically to detect mute changes
        this._checkMuteState();
      }
    }
  }
  
  _checkMuteState() {
    if (!this.player || !this.player.iframe) {
      // Stop checking if player is destroyed
      if (this.muteCheckInterval) {
        clearTimeout(this.muteCheckInterval);
        this.muteCheckInterval = null;
      }
      return;
    }
    
    const src = this.player.iframe.src || this.player.iframe.getAttribute('src');
    
    // If URL doesn't have mute=1, user has unmuted
    if (src && !src.includes('mute=1')) {
      sessionStorage.setItem('video_unmuted', 'true');
    }
    
    // Check again in 500ms
    this.muteCheckInterval = setTimeout(() => this._checkMuteState(), 500);
  }
  
  _handlePostMessage(event) {
    // Handle postMessage events from Vimeo player if needed
    if (event.data && event.data.event) {
      if (event.data.event === 'play' && event.data.data && event.data.data.volume > 0) {
        sessionStorage.setItem('video_unmuted', 'true');
      }
    }
  }

}
